import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md border border-zinc-800 p-10">
        <Link to="/" className="font-display text-2xl font-bold tracking-tight block mb-2">
          mixo<span className="text-red-500"> </span>vap
        </Link>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-8">Mot de passe oublié</div>
        {sent ? (
          <div className="text-center py-6" data-testid="forgot-success">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <div className="font-display text-lg font-semibold">Email envoyé</div>
            <p className="text-sm text-zinc-400 mt-3">
              Si un compte existe pour <b>{email}</b>, un email vient d'être envoyé avec un lien de réinitialisation valable 1 heure.
            </p>
            <Link to="/login" className="inline-block mt-6 text-sm text-red-500 hover:text-red-400">Retour à la connexion</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <p className="text-sm text-zinc-400 leading-relaxed">
              Entrez l'email de votre compte. Un lien de réinitialisation vous sera envoyé.
            </p>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Email</label>
              <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-none bg-zinc-900 border-zinc-800 focus-visible:ring-1 focus-visible:ring-red-500 focus-visible:ring-offset-0 focus-visible:border-red-500" />
            </div>
            <Button data-testid="forgot-submit" disabled={loading} className="w-full bg-red-500 hover:bg-red-600 rounded-none">
              {loading ? "Envoi…" : "Envoyer le lien"}
            </Button>
            <div className="text-sm text-zinc-500 text-center">
              <Link to="/login" className="text-red-500 hover:text-red-400">Retour à la connexion</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== password2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (password.length < 6) { toast.error("Minimum 6 caractères"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Mot de passe réinitialisé. Vous pouvez vous connecter.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md border border-zinc-800 p-10">
        <Link to="/" className="font-display text-2xl font-bold tracking-tight block mb-2">
          mixo<span className="text-red-500"> </span>vap
        </Link>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-8">Nouveau mot de passe</div>
        {!token ? (
          <div className="text-sm text-red-400">Lien invalide. <Link to="/forgot-password" className="underline">Faire une nouvelle demande</Link></div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Nouveau mot de passe (min. 6 caractères)</label>
              <Input data-testid="reset-password" type="password" minLength={6} required value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-none bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Confirmer</label>
              <Input data-testid="reset-password2" type="password" minLength={6} required value={password2} onChange={(e) => setPassword2(e.target.value)}
                className="rounded-none bg-zinc-900 border-zinc-800" />
            </div>
            <Button data-testid="reset-submit" disabled={loading} className="w-full bg-red-500 hover:bg-red-600 rounded-none">
              {loading ? "Enregistrement…" : "Réinitialiser mon mot de passe"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
